<template>
  <div class="normativa-container">
    <app-header rootClassName="header-root-class-name7"></app-header>
    <h1 class="normativa-text">Portal de Transparencia</h1>

    <!-- Secciones del Portal de Transparencia (visible when showAllSections is true) -->
    <div class="portal-sections" v-if="showAllSections">
      <div class="section-card" v-for="section in sections" :key="section.id" @click="setActiveSection(section.id)">
        <div class="section-icon">
          <i :class="[section.icon, 'section-icon-image']"></i>
        </div>
        <div class="section-content">
          <h2 class="section-title">{{ section.title }}</h2>
          <p class="section-description">{{ section.description }}</p>
        </div>
      </div>
    </div>

    <!-- Sección 1: Información de la Entidad -->
    <div class="section-detail" v-if="activeSection === 'informacion-entidad' && !showAllSections">
      <div class="section-header">
        <button class="back-button" @click="goBackToAllSections">
          <i class="pi pi-arrow-left"></i> Volver
        </button>
        <h2 class="section-heading">Información de la Entidad</h2>
        <p class="section-caption">
          Misión, visión, estructura orgánica, directorios y más información institucional.
        </p>
      </div>

      <div class="subsections-grid">
        <div class="subsection-card" v-for="subsection in informacionEntidadSubsections" :key="subsection.id">
          <h3 class="subsection-title">{{ subsection.title }}</h3>
          <p class="subsection-description">{{ subsection.description }}</p>
          
          <!-- Documentos de esta subsección -->
          <div class="subsection-documents" v-if="getSectionDocuments(subsection.id).length > 0">
            <div class="document-icons">
              <div class="document-icon-wrapper" v-for="(doc, index) in getSectionDocuments(subsection.id)" :key="index" @click="viewDocument(doc)" :title="doc.name">
                <div class="document-icon-large">
                  <i :class="getDocumentIcon(doc.type)"></i>
                </div>
                <span class="document-icon-name">{{ doc.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Documentos de la sección -->
      <div class="section-documents" v-if="getSectionDocuments('informacion-entidad').length > 0">
        <h3 class="documents-title">Documentos</h3>
        <div class="documents-grid">
          <div class="document-card" v-for="(doc, index) in getSectionDocuments('informacion-entidad')" :key="index">
            <div class="document-icon">
              <i :class="getDocumentIcon(doc.type)"></i>
            </div>
            <div class="document-info">
              <h4 class="document-name">{{ doc.name }}</h4>
              <p class="document-meta">{{ formatFileSize(doc.size) }} - {{ formatDate(doc.date) }}</p>
            </div>
            <div class="document-actions">
              <button class="document-action-button" @click="downloadDocument(doc)">
                <i class="pi pi-download"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección 2: Normativa -->
    <div class="section-detail" v-if="activeSection === 'normativa' && !showAllSections">
      <div class="section-header">
        <button class="back-button" @click="goBackToAllSections">
          <i class="pi pi-arrow-left"></i> Volver
        </button>
        <h2 class="section-heading">Normativa</h2>
        <p class="section-caption">
          Leyes, decretos, resoluciones y demás normativa aplicable a la entidad.
        </p>
      </div>

      <div class="subsections-grid">
        <div class="subsection-card" v-for="subsection in normativaSubsections" :key="subsection.id">
          <h3 class="subsection-title">{{ subsection.title }}</h3>
          <p class="subsection-description">{{ subsection.description }}</p>
          
          <!-- Documentos para subsecciones específicas -->
          <div class="subsection-documents" v-if="getSectionDocuments(subsection.id).length > 0">
            <div class="document-icons">
              <div class="document-icon-wrapper" v-for="(doc, index) in getSectionDocuments(subsection.id)" :key="index" @click="viewDocument(doc)" :title="doc.name">
                <div class="document-icon-large">
                  <i :class="getDocumentIcon(doc.type)"></i>
                </div>
                <span class="document-icon-name">{{ doc.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Documentos de la sección -->
      <div class="section-documents" v-if="getSectionDocuments('normativa').length > 0">
        <h3 class="documents-title">Documentos</h3>
        <div class="documents-grid">
          <div class="document-card" v-for="(doc, index) in getSectionDocuments('normativa')" :key="index">
            <div class="document-icon">
              <i :class="getDocumentIcon(doc.type)"></i>
            </div>
            <div class="document-info">
              <h4 class="document-name">{{ doc.name }}</h4>
              <p class="document-meta">{{ formatFileSize(doc.size) }} - {{ formatDate(doc.date) }}</p>
            </div>
            <div class="document-actions">
              <button class="document-action-button" @click="downloadDocument(doc)">
                <i class="pi pi-download"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección 3: Contratación -->
    <div class="section-detail" v-if="activeSection === 'contratacion' && !showAllSections">
      <div class="section-header">
        <button class="back-button" @click="goBackToAllSections">
          <i class="pi pi-arrow-left"></i> Volver
        </button>
        <h2 class="section-heading">Contratación</h2>
        <p class="section-caption">
          Plan anual de adquisiciones, información contractual y ejecución de contratos.
        </p>
      </div>

      <div class="subsections-grid">
        <div class="subsection-card" v-for="subsection in contratacionSubsections" :key="subsection.id">
          <h3 class="subsection-title">{{ subsection.title }}</h3>
          <p class="subsection-description">{{ subsection.description }}</p>
          
          <!-- Documentos para subsecciones específicas -->
          <div class="subsection-documents" v-if="getSectionDocuments(subsection.id).length > 0">
            <div class="document-icons">
              <div class="document-icon-wrapper" v-for="(doc, index) in getSectionDocuments(subsection.id)" :key="index" @click="viewDocument(doc)" :title="doc.name">
                <div class="document-icon-large">
                  <i :class="getDocumentIcon(doc.type)"></i>
                </div>
                <span class="document-icon-name">{{ doc.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Documentos de la sección -->
      <div class="section-documents" v-if="getSectionDocuments('contratacion').length > 0">
        <h3 class="documents-title">Documentos</h3>
        <div class="documents-grid">
          <div class="document-card" v-for="(doc, index) in getSectionDocuments('contratacion')" :key="index">
            <div class="document-icon">
              <i :class="getDocumentIcon(doc.type)"></i>
            </div>
            <div class="document-info">
              <h4 class="document-name">{{ doc.name }}</h4>
              <p class="document-meta">{{ formatFileSize(doc.size) }} - {{ formatDate(doc.date) }}</p>
            </div>
            <div class="document-actions">
              <button class="document-action-button" @click="downloadDocument(doc)">
                <i class="pi pi-download"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección 4: Planeación -->
    <div class="section-detail" v-if="activeSection === 'planeacion' && !showAllSections">
      <div class="section-header">
        <button class="back-button" @click="goBackToAllSections">
          <i class="pi pi-arrow-left"></i> Volver
        </button>
        <h2 class="section-heading">Planeación, Presupuesto e Informes</h2>
        <p class="section-caption">
          Presupuesto, planes de acción, proyectos de inversión e informes.
        </p>
      </div>

      <div class="subsections-grid">
        <div class="subsection-card" v-for="subsection in planeacionSubsections" :key="subsection.id">
          <h3 class="subsection-title">{{ subsection.title }}</h3>
          <p class="subsection-description">{{ subsection.description }}</p>
        </div>
      </div>

      <!-- Documentos de la sección -->
      <div class="section-documents" v-if="getSectionDocuments('planeacion').length > 0">
        <h3 class="documents-title">Documentos</h3>
        <div class="documents-grid">
          <div class="document-card" v-for="(doc, index) in getSectionDocuments('planeacion')" :key="index">
            <div class="document-icon">
              <i :class="getDocumentIcon(doc.type)"></i>
            </div>
            <div class="document-info">
              <h4 class="document-name">{{ doc.name }}</h4>
              <p class="document-meta">{{ formatFileSize(doc.size) }} - {{ formatDate(doc.date) }}</p>
            </div>
            <div class="document-actions">
              <button class="document-action-button" @click="downloadDocument(doc)">
                <i class="pi pi-download"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección 5: Trámites -->
    <div class="section-detail" v-if="activeSection === 'tramites' && !showAllSections">
      <div class="section-header">
        <button class="back-button" @click="goBackToAllSections">
          <i class="pi pi-arrow-left"></i> Volver
        </button>
        <h2 class="section-heading">Trámites</h2>
        <p class="section-caption">
          Información sobre trámites y servicios ofrecidos por la entidad.
        </p>
      </div>

      <div class="subsections-grid">
        <div class="subsection-card" v-for="subsection in tramitesSubsections" :key="subsection.id">
          <h3 class="subsection-title">{{ subsection.title }}</h3>
          <p class="subsection-description">{{ subsection.description }}</p>
        </div>
      </div>

      <!-- Documentos de la sección -->
      <div class="section-documents" v-if="getSectionDocuments('tramites').length > 0">
        <h3 class="documents-title">Documentos</h3>
        <div class="documents-grid">
          <div class="document-card" v-for="(doc, index) in getSectionDocuments('tramites')" :key="index">
            <div class="document-icon">
              <i :class="getDocumentIcon(doc.type)"></i>
            </div>
            <div class="document-info">
              <h4 class="document-name">{{ doc.name }}</h4>
              <p class="document-meta">{{ formatFileSize(doc.size) }} - {{ formatDate(doc.date) }}</p>
            </div>
            <div class="document-actions">
              <button class="document-action-button" @click="downloadDocument(doc)">
                <i class="pi pi-download"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección 6: Participa -->
    <div class="section-detail" v-if="activeSection === 'participa' && !showAllSections">
      <div class="section-header">
        <button class="back-button" @click="goBackToAllSections">
          <i class="pi pi-arrow-left"></i> Volver
        </button>
        <h2 class="section-heading">Participa</h2>
        <p class="section-caption">
          Mecanismos de participación ciudadana y rendición de cuentas.
        </p>
      </div>

      <div class="subsections-grid">
        <div class="subsection-card" v-for="subsection in participaSubsections" :key="subsection.id">
          <h3 class="subsection-title">{{ subsection.title }}</h3>
          <p class="subsection-description">{{ subsection.description }}</p>
        </div>
      </div>

      <!-- Documentos de la sección -->
      <div class="section-documents" v-if="getSectionDocuments('participa').length > 0">
        <h3 class="documents-title">Documentos</h3>
        <div class="documents-grid">
          <div class="document-card" v-for="(doc, index) in getSectionDocuments('participa')" :key="index">
            <div class="document-icon">
              <i :class="getDocumentIcon(doc.type)"></i>
            </div>
            <div class="document-info">
              <h4 class="document-name">{{ doc.name }}</h4>
              <p class="document-meta">{{ formatFileSize(doc.size) }} - {{ formatDate(doc.date) }}</p>
            </div>
            <div class="document-actions">
              <button class="document-action-button" @click="downloadDocument(doc)">
                <i class="pi pi-download"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección 7: Datos Abiertos -->
    <div class="section-detail" v-if="activeSection === 'datos-abiertos' && !showAllSections">
      <div class="section-header">
        <button class="back-button" @click="goBackToAllSections">
          <i class="pi pi-arrow-left"></i> Volver
        </button>
        <h2 class="section-heading">Datos Abiertos</h2>
        <p class="section-caption">
          Instrumentos de gestión de información y datos abiertos.
        </p>
      </div>

      <div class="subsections-grid">
        <div class="subsection-card" v-for="subsection in datosAbiertosSubsections" :key="subsection.id">
          <h3 class="subsection-title">{{ subsection.title }}</h3>
          <p class="subsection-description">{{ subsection.description }}</p>
        </div>
      </div>

      <!-- Documentos de la sección -->
      <div class="section-documents" v-if="getSectionDocuments('datos-abiertos').length > 0">
        <h3 class="documents-title">Documentos</h3>
        <div class="documents-grid">
          <div class="document-card" v-for="(doc, index) in getSectionDocuments('datos-abiertos')" :key="index">
            <div class="document-icon">
              <i :class="getDocumentIcon(doc.type)"></i>
            </div>
            <div class="document-info">
              <h4 class="document-name">{{ doc.name }}</h4>
              <p class="document-meta">{{ formatFileSize(doc.size) }} - {{ formatDate(doc.date) }}</p>
            </div>
            <div class="document-actions">
              <button class="document-action-button" @click="downloadDocument(doc)">
                <i class="pi pi-download"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección 8: Grupos de Interés -->
    <div class="section-detail" v-if="activeSection === 'grupos-interes' && !showAllSections">
      <div class="section-header">
        <button class="back-button" @click="goBackToAllSections">
          <i class="pi pi-arrow-left"></i> Volver
        </button>
        <h2 class="section-heading">Grupos de Interés</h2>
        <p class="section-caption">
          Información específica para niños, niñas, adolescentes y mujeres.
        </p>
      </div>

      <div class="subsections-grid">
        <div class="subsection-card" v-for="subsection in gruposInteresSubsections" :key="subsection.id">
          <h3 class="subsection-title">{{ subsection.title }}</h3>
          <p class="subsection-description">{{ subsection.description }}</p>
        </div>
      </div>

      <!-- Documentos de la sección -->
      <div class="section-documents" v-if="getSectionDocuments('grupos-interes').length > 0">
        <h3 class="documents-title">Documentos</h3>
        <div class="documents-grid">
          <div class="document-card" v-for="(doc, index) in getSectionDocuments('grupos-interes')" :key="index">
            <div class="document-icon">
              <i :class="getDocumentIcon(doc.type)"></i>
            </div>
            <div class="document-info">
              <h4 class="document-name">{{ doc.name }}</h4>
              <p class="document-meta">{{ formatFileSize(doc.size) }} - {{ formatDate(doc.date) }}</p>
            </div>
            <div class="document-actions">
              <button class="document-action-button" @click="downloadDocument(doc)">
                <i class="pi pi-download"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PDF Viewer Dialog -->
    <div class="pdf-viewer-overlay" v-if="showPdfViewer" @click="closePdfViewer">
      <div class="pdf-viewer-dialog" @click.stop>
        <div class="pdf-viewer-header">
          <h3>{{ currentPdfName }}</h3>
          <button class="close-button" @click="closePdfViewer">&times;</button>
        </div>
        <div class="pdf-viewer-content">
          <iframe :src="currentPdfUrl" class="pdf-iframe" frameborder="0"></iframe>
        </div>
        <div class="pdf-viewer-footer">
        </div>
      </div>
    </div>
    
    <!-- Upload Dialog -->
    <div class="upload-dialog-overlay" v-if="showUploadDialog" @click="cancelUpload">
      <div class="upload-dialog" @click.stop>
        <div class="upload-dialog-header">
          <h3>Subir Documento</h3>
          <button class="close-button" @click="cancelUpload">&times;</button>
        </div>
        <div class="upload-dialog-content">
          <p class="upload-section-name">Sección: <strong>{{ getActiveSectionName() }}</strong></p>

          <div class="upload-form">
            <div class="form-group">
              <label for="document-title">Título del documento</label>
              <input id="document-title" v-model="uploadForm.title" class="form-control" placeholder="Ingrese un título para el documento" />
            </div>

            <div class="form-group">
              <label for="document-description">Descripción (opcional)</label>
              <textarea id="document-description" v-model="uploadForm.description" class="form-control" rows="3" placeholder="Ingrese una descripción"></textarea>
            </div>

            <div class="form-group">
              <label>Archivo</label>
              <div class="file-upload-container">
                <input type="file" ref="fileInput" @change="handleFileSelected" class="file-input" />
                <button class="file-select-button" @click="triggerFileInput">
                  <i class="pi pi-file"></i> Seleccionar archivo
                </button>
                <span v-if="uploadForm.file" class="selected-filename">{{ uploadForm.file.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="upload-dialog-footer">
          <button class="cancel-button" @click="cancelUpload">Cancelar</button>
          <button class="upload-submit-button" @click="uploadDocument" :disabled="!uploadForm.file || !uploadForm.title">
            <span v-if="uploading">Subiendo...</span>
            <span v-else>Subir</span>
          </button>
        </div>
      </div>
    </div>

    <h1 class="normativa-quote">
      &quot;Una Educación Incluyente y de Oportunidades&quot;
    </h1>
    <app-footer rootClassName="footer-root-class-name"></app-footer>
  </div>
</template>

<script>
import AppHeader from '../components/header'
import AppFooter from '../components/footer'
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useContentStore } from '../store/content'

export default {
  name: 'Normativa',
  components: {
    AppHeader,
    AppFooter
  },
  setup() {
    const router = useRouter()
    const contentStore = useContentStore()
    const activeSection = ref('normativa')
    const showAllSections = ref(true)
    const showUploadDialog = ref(false)
    const showPdfViewer = ref(false)
    const currentPdfUrl = ref('')
    const currentPdfName = ref('')
    const uploading = ref(false)
    const currentSection = ref('')
    const fileInput = ref(null)
    const loadedDocuments = ref({})

    // Datos de documentos por sección
    const documents = ref({
      'informacion-entidad': [],
      'normativa': [],
      'contratacion': [],
      'planeacion': [],
      'tramites': [],
      'participa': [],
      'datos-abiertos': [],
      'grupos-interes': []
    })

    // Documentos predefinidos
    const predefinedDocuments = {
      'normativa-aplicable': [
        {
          id: 'anexo-8',
          name: 'ANEXO 8. SISTEMA INSTITUCIONAL DE EVALUACION DE ESTUDIANTES',
          description: 'Sistema Institucional de Evaluación de Estudiantes',
          filename: 'ANEXO 8.  SISTEMA INSTITUCIONAL DE EVALUACION DE ESTUDIANTES.pdf',
          type: 'application/pdf',
          size: 875658,
          date: new Date().toISOString(),
          url: '/documentos/ANEXO 8.  SISTEMA INSTITUCIONAL DE EVALUACION DE ESTUDIANTES.pdf'
        },
        {
          id: 'pei',
          name: 'PEI - Proyecto Educativo Institucional',
          description: 'Proyecto Educativo Institucional del Colegio Tasco',
          filename: 'PEI.pdf',
          type: 'application/pdf',
          size: 2105603,
          date: new Date().toISOString(),
          url: '/documentos/PEI.pdf'
        }
      ],
      'politicas-lineamientos': [
        {
          id: 'calendario-academico',
          name: 'Calendario Académico 2025',
          description: 'Calendario Académico para el año 2025',
          filename: 'Calendario Academico 2025.pdf',
          type: 'application/pdf',
          size: 238829,
          date: new Date().toISOString(),
          url: '/documentos/Calendario Academico 2025.pdf'
        },
        {
          id: 'manual-convivencia',
          name: 'Manual de Convivencia',
          description: 'Manual de Convivencia Institucional',
          filename: 'MANUAL DE CONVIVENCIA.pdf',
          type: 'application/pdf',
          size: 1001744,
          date: new Date().toISOString(),
          url: '/documentos/MANUAL DE CONVIVENCIA.pdf'
        }
      ]
    }

    // Formulario de carga
    const uploadForm = reactive({
      title: '',
      description: '',
      file: null
    })

    const sections = ref([
      {
        id: 'informacion-entidad',
        title: 'Información de la Entidad',
        icon: 'pi pi-info-circle',
        description: 'Misión, visión, estructura orgánica, directorios y más información institucional.',
        route: '/portal-transparencia/informacion-entidad'
      },
      {
        id: 'normativa',
        title: 'Normativa',
        icon: 'pi pi-book',
        description: 'Leyes, decretos, resoluciones y demás normativa aplicable a la entidad.',
        route: '/portal-transparencia/normativa'
      },
      {
        id: 'contratacion',
        title: 'Contratación',
        icon: 'pi pi-file',
        description: 'Plan anual de adquisiciones, información contractual y ejecución de contratos.',
        route: '/portal-transparencia/contratacion'
      },
      {
        id: 'planeacion',
        title: 'Planeación y Presupuesto',
        icon: 'pi pi-chart-line',
        description: 'Presupuesto, planes de acción, proyectos de inversión e informes.',
        route: '/portal-transparencia/planeacion'
      },
      {
        id: 'tramites',
        title: 'Trámites',
        icon: 'pi pi-check-square',
        description: 'Información sobre trámites y servicios ofrecidos por la entidad.',
        route: '/portal-transparencia/tramites'
      },
      {
        id: 'participa',
        title: 'Participa',
        icon: 'pi pi-users',
        description: 'Mecanismos de participación ciudadana y rendición de cuentas.',
        route: '/portal-transparencia/participa'
      },
      {
        id: 'datos-abiertos',
        title: 'Datos Abiertos',
        icon: 'pi pi-database',
        description: 'Instrumentos de gestión de información y datos abiertos.',
        route: '/portal-transparencia/datos-abiertos'
      },
      {
        id: 'grupos-interes',
        title: 'Grupos de Interés',
        icon: 'pi pi-heart',
        description: 'Información específica para niños, niñas, adolescentes y mujeres.',
        route: '/portal-transparencia/grupos-interes'
      }
    ])

    // Subsecciones de Información de la Entidad
    const informacionEntidadSubsections = ref([
      { id: 'mision-vision', title: '1.1 Misión, visión, funciones y deberes', description: 'Información sobre la misión, visión, funciones y deberes de la institución.' },
      { id: 'estructura-organica', title: '1.2 Estructura orgánica - Organigrama', description: 'Organigrama y estructura organizacional de la institución.' },
      { id: 'mapas-procesos', title: '1.3 Mapas y Cartas descriptivas de los procesos', description: 'Documentación sobre los procesos institucionales.' },
      { id: 'directorio-institucional', title: '1.4 Directorio Institucional', description: 'Información de contacto de la institución.' },
      { id: 'directorio-servidores', title: '1.5 Directorio de servidores públicos', description: 'Directorio de funcionarios y servidores públicos.' },
      { id: 'directorio-entidades', title: '1.6 Directorio de entidades', description: 'Directorio de entidades relacionadas.' }
    ])

    // Subsecciones de Normativa
    const normativaSubsections = ref([
      { id: 'leyes', title: '2.1.1 Leyes', description: 'Leyes aplicables a nuestra institución educativa.' },
      { id: 'decreto-unico', title: '2.1.2 Decreto Único Reglamentario', description: 'Información sobre el decreto único reglamentario aplicable al sector educativo.' },
      { id: 'normativa-aplicable', title: '2.1.3 Normativa aplicable', description: 'Listado de normativas aplicables a nuestra institución.' },
      { id: 'diario-oficial', title: '2.1.4 Vínculo al Diario o Gaceta Oficial', description: 'Enlaces al diario oficial donde se publican las normativas relevantes.' },
      { id: 'politicas-lineamientos', title: '2.1.5 Políticas, lineamientos y manuales', description: 'Documentación sobre políticas institucionales y lineamientos operativos.' },
      { id: 'agenda-regulatoria', title: '2.1.6 Agenda Regulatoria', description: 'Información sobre la agenda regulatoria aplicable.' },
      { id: 'suin', title: '2.2.1 Sistema Único de Información Normativa – SUIN', description: 'Enlaces y recursos relacionados con el SUIN.' },
      { id: 'sistema-busqueda', title: '2.2.2 Sistema de búsquedas de normas', description: 'Herramientas para la búsqueda de normativas aplicables.' }
    ])

    // Subsecciones de Contratación
    const contratacionSubsections = ref([
      { id: 'plan-adquisiciones', title: '3.1 Plan Anual de Adquisiciones', description: 'Plan anual de adquisiciones de la institución.' },
      { id: 'info-contractual', title: '3.2 Publicación de la información contractual', description: 'Información sobre contratos y procesos de contratación.' },
      { id: 'ejecucion-contratos', title: '3.3 Publicación de la ejecución de los contratos', description: 'Información sobre la ejecución de contratos.' },
      { id: 'manual-contratacion', title: '3.4 Manual de contratación', description: 'Manual de procedimientos de contratación.' },
      { id: 'formatos-contratos', title: '3.5 Formatos o modelos de contratos', description: 'Formatos y modelos de contratos utilizados.' }
    ])

    // Subsecciones de Planeación
    const planeacionSubsections = ref([
      { id: 'presupuesto-general', title: '4.1 Presupuesto general', description: 'Información sobre el presupuesto general de la institución.' },
      { id: 'ejecucion-presupuestal', title: '4.2 Ejecución presupuestal', description: 'Información sobre la ejecución del presupuesto.' },
      { id: 'plan-accion', title: '4.3 Plan de Acción', description: 'Plan de acción institucional.' },
      { id: 'proyectos-inversion', title: '4.4 Proyectos de Inversión', description: 'Información sobre proyectos de inversión.' },
      { id: 'informes-empalme', title: '4.5 Informes de empalme', description: 'Informes de empalme entre administraciones.' }
    ])

    // Subsecciones de Trámites
    const tramitesSubsections = ref([
      { id: 'tramites-servicios', title: '5.1 Trámites y servicios', description: 'Información sobre trámites y servicios ofrecidos por la institución.' }
    ])

    // Subsecciones de Participa
    const participaSubsections = ref([
      { id: 'participa-descripcion', title: '6.1 Participa (Descripción general)', description: 'Descripción general de los mecanismos de participación.' },
      { id: 'diagnostico-necesidades', title: '6.2 Participación para el diagnóstico de necesidades', description: 'Mecanismos de participación para el diagnóstico de necesidades.' },
      { id: 'planeacion-participativa', title: '6.3 Planeación y presupuesto participativo', description: 'Información sobre planeación y presupuesto participativo.' },
      { id: 'consulta-ciudadana', title: '6.4 Consulta ciudadana', description: 'Mecanismos de consulta ciudadana.' }
    ])

    // Subsecciones de Datos Abiertos
    const datosAbiertosSubsections = ref([
      { id: 'gestion-informacion', title: '7.1 Instrumentos de gestión de la información', description: 'Instrumentos para la gestión de la información pública.' },
      { id: 'datos-abiertos', title: '7.2 Sección de Datos Abiertos', description: 'Datos abiertos disponibles para consulta y uso.' }
    ])

    // Subsecciones de Grupos de Interés
    const gruposInteresSubsections = ref([
      { id: 'info-ninos', title: '8.1 Información para niños, niñas y adolescentes', description: 'Información específica para niños, niñas y adolescentes.' },
      { id: 'info-mujeres', title: '8.2 Información para Mujeres', description: 'Información específica para mujeres.' }
    ])

    // Funciones para manejar la navegación
    const setActiveSection = async (sectionId) => {
      activeSection.value = sectionId
      showAllSections.value = false
      
      // Cargar documentos de las subsecciones de esta sección
      console.log(`Cargando documentos para la sección: ${sectionId}`)
      
      if (sectionId === 'informacion-entidad') {
        for (const subsection of informacionEntidadSubsections.value) {
          await loadDocumentsFromBackend(subsection.id)
        }
      } else if (sectionId === 'normativa') {
        for (const subsection of normativaSubsections.value) {
          await loadDocumentsFromBackend(subsection.id)
        }
      } else if (sectionId === 'contratacion') {
        // Cargar documentos para cada subsección específica
        await loadDocumentsFromBackend('contratacion') // Para la sección general
        await loadDocumentsFromBackend('plan-adquisiciones') // Para plan de adquisiciones
        await loadDocumentsFromBackend('info-contractual')
        await loadDocumentsFromBackend('ejecucion-contratos')
        await loadDocumentsFromBackend('manual-contratacion')
        await loadDocumentsFromBackend('formatos-contratos')
      } else if (sectionId === 'planeacion') {
        for (const subsection of planeacionSubsections.value) {
          await loadDocumentsFromBackend(subsection.id)
        }
      } else if (sectionId === 'tramites') {
        for (const subsection of tramitesSubsections.value) {
          await loadDocumentsFromBackend(subsection.id)
        }
      } else if (sectionId === 'participa') {
        for (const subsection of participaSubsections.value) {
          await loadDocumentsFromBackend(subsection.id)
        }
      } else if (sectionId === 'datos-abiertos') {
        for (const subsection of datosAbiertosSubsections.value) {
          await loadDocumentsFromBackend(subsection.id)
        }
      } else if (sectionId === 'grupos-interes') {
        for (const subsection of gruposInteresSubsections.value) {
          await loadDocumentsFromBackend(subsection.id)
        }
      }
    }

    const goBackToAllSections = () => {
      showAllSections.value = true
    }

    const viewSubsection = (subsectionId) => {
      console.log(`Viewing subsection: ${subsectionId}`)
    }

    // Funciones para manejar la carga de documentos
    const openUploadDialog = (sectionId) => {
      currentSection.value = sectionId
      showUploadDialog.value = true
      resetUploadForm()
    }

    const resetUploadForm = () => {
      uploadForm.title = ''
      uploadForm.description = ''
      uploadForm.file = null
    }

    const cancelUpload = () => {
      showUploadDialog.value = false
      resetUploadForm()
    }

    const triggerFileInput = () => {
      fileInput.value.click()
    }

    const handleFileSelected = (event) => {
      const file = event.target.files[0]
      if (file) {
        uploadForm.file = file
      }
    }

    const uploadDocument = () => {
      if (!uploadForm.file || !uploadForm.title) return

      uploading.value = true

      // Simular carga de archivo (en una aplicación real, esto sería una llamada a una API)
      setTimeout(() => {
        const newDocument = {
          id: `doc-${Date.now()}`,
          name: uploadForm.title,
          description: uploadForm.description,
          filename: uploadForm.file.name,
          type: uploadForm.file.type,
          size: uploadForm.file.size,
          date: new Date().toISOString(),
          url: URL.createObjectURL(uploadForm.file) // En una aplicación real, esto sería una URL de servidor
        }

        documents.value[currentSection.value].push(newDocument)

        showUploadDialog.value = false
        uploading.value = false
        resetUploadForm()

        // Mostrar mensaje de éxito (en una aplicación real, usarías un sistema de notificaciones)
        alert('Documento subido correctamente')
      }, 1500)
    }

    const getSectionDocuments = (sectionId) => {
      // Combinar documentos del backend con los predefinidos
      const backendDocs = loadedDocuments.value[sectionId] || []
      const predefinedDocs = predefinedDocuments[sectionId] || []
      const uploadedDocs = documents.value[sectionId] || []
      
      // Combinar todos los documentos
      return [...backendDocs, ...predefinedDocs, ...uploadedDocs]
    }
    
    // Función para cargar documentos desde el backend
    const loadDocumentsFromBackend = async (sectionId) => {
      try {
        const docs = await contentStore.getSectionDocuments(sectionId)
        if (docs && docs.length > 0) {
          loadedDocuments.value[sectionId] = docs
        }
      } catch (error) {
        // Silenciar errores 403 en vista pública (no autenticada)
        if (error.response?.status === 403) {
          console.log(`Vista pública: No se pueden cargar documentos de ${sectionId} sin autenticación`)
        } else {
          console.error(`Error cargando documentos para ${sectionId}:`, error)
        }
      }
    }

    const getActiveSectionName = () => {
      const section = sections.value.find(s => s.id === currentSection.value)
      return section ? section.title : ''
    }

    const downloadDocument = (doc) => {
      // Para documentos predefinidos, abrir directamente la URL
      if (doc.url.startsWith('/documentos/')) {
        window.open(doc.url, '_blank')
      } else {
        // Para documentos subidos por el usuario, usar la URL generada
        window.open(doc.url, '_blank')
      }
    }

    const viewDocument = (doc) => {
      // Abrir el documento en el visor inmersivo
      currentPdfUrl.value = doc.url
      currentPdfName.value = doc.name
      showPdfViewer.value = true
    }
    
    const closePdfViewer = () => {
      showPdfViewer.value = false
      currentPdfUrl.value = ''
      currentPdfName.value = ''
    }

    const getDocumentIcon = (type) => {
      if (type.includes('pdf')) {
        return 'pi pi-file-pdf'
      } else if (type.includes('word') || type.includes('document')) {
        return 'pi pi-file-word'
      } else if (type.includes('excel') || type.includes('sheet')) {
        return 'pi pi-file-excel'
      } else if (type.includes('powerpoint') || type.includes('presentation')) {
        return 'pi pi-file-powerpoint'
      } else if (type.includes('image')) {
        return 'pi pi-image'
      } else {
        return 'pi pi-file'
      }
    }

    const formatFileSize = (bytes) => {
      if (bytes < 1024) {
        return bytes + ' B'
      } else if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(1) + ' KB'
      } else {
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
      }
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString()
    }

    // Set default active section on mount
    onMounted(async () => {
      // Default to normativa section
      activeSection.value = 'normativa'
      showAllSections.value = true
      
      // Inicializar tipos de documento
      await contentStore.initializeDocumentTypes()
      
      // Cargar documentos de todas las subsecciones de normativa
      const subsectionIds = normativaSubsections.value.map(s => s.id)
      for (const id of subsectionIds) {
        await loadDocumentsFromBackend(id)
      }
      
      // También cargar documentos de información de la entidad
      const infoSubsectionIds = informacionEntidadSubsections.value.map(s => s.id)
      for (const id of infoSubsectionIds) {
        await loadDocumentsFromBackend(id)
      }
    })

    return {
      sections,
      activeSection,
      showAllSections,
      informacionEntidadSubsections,
      normativaSubsections,
      contratacionSubsections,
      planeacionSubsections,
      tramitesSubsections,
      participaSubsections,
      datosAbiertosSubsections,
      gruposInteresSubsections,
      setActiveSection,
      goBackToAllSections,
      viewSubsection,
      showUploadDialog,
      showPdfViewer,
      currentPdfUrl,
      currentPdfName,
      uploadForm,
      uploading,
      currentSection,
      fileInput,
      openUploadDialog,
      cancelUpload,
      triggerFileInput,
      handleFileSelected,
      uploadDocument,
      getSectionDocuments,
      getActiveSectionName,
      downloadDocument,
      viewDocument,
      closePdfViewer,
      getDocumentIcon,
      formatFileSize,
      formatDate
    }
  },
  metaInfo: {
    title: 'Normativa - ColegioTasco',
    meta: [
      {
        property: 'og:title',
        content: 'Normativa - ColegioTasco',
      },
    ],
  },
}
</script>

<style scoped>
.normativa-container {
  width: 100%;
  display: flex;
  overflow: auto;
  min-height: 100vh;
  align-items: center;
  flex-direction: column;
  justify-content: flex-start;
}

.normativa-text {
  font-size: 3rem;
  margin-top: var(--dl-space-space-threeunits);
  margin-bottom: var(--dl-space-space-threeunits);
}

.portal-sections {
  width: 100%;
  display: flex;
  max-width: var(--dl-size-size-default);
  flex-wrap: wrap;
  margin-top: var(--dl-space-space-twounits);
  margin-bottom: var(--dl-space-space-twounits);
  justify-content: center;
}

.section-card {
  width: 300px;
  display: flex;
  padding: var(--dl-space-space-unit);
  margin: var(--dl-space-space-unit);
  align-items: center;
  border-radius: 8px;
  flex-direction: column;
  justify-content: center;
  background-color: var(--dl-color-gray-white);
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.section-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
}

.section-icon {
  display: flex;
  padding: var(--dl-space-space-unit);
  align-items: center;
  border-radius: 50%;
  margin-bottom: var(--dl-space-space-unit);
  justify-content: center;
  background-color: var(--dl-color-gray-900);
}

.section-icon-image {
  color: var(--dl-color-gray-white);
  font-size: 24px;
}

.section-content {
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.section-title {
  font-size: 1.5rem;
  text-align: center;
  margin-bottom: var(--dl-space-space-halfunit);
}

.section-description {
  color: var(--dl-color-gray-500);
  text-align: center;
  margin-bottom: var(--dl-space-space-unit);
}

.section-detail {
  width: 100%;
  display: flex;
  max-width: var(--dl-size-size-default);
  margin-top: var(--dl-space-space-fourunits);
  align-items: center;
  margin-bottom: var(--dl-space-space-fourunits);
  flex-direction: column;
  justify-content: center;
}

.section-header {
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: var(--dl-space-space-twounits);
  flex-direction: column;
  position: relative;
}

.section-heading {
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: var(--dl-space-space-unit);
}

.section-caption {
  color: var(--dl-color-gray-500);
  font-size: 1.1rem;
  max-width: 600px;
  text-align: center;
  margin-bottom: var(--dl-space-space-twounits);
}

.upload-button {
  padding: 10px 20px;
  background-color: #2196F3; /* Changed to blue for better visibility */
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
  font-weight: bold;
  font-size: 1rem;
}

.upload-button:hover {
  background-color: #1976D2; /* Darker blue on hover */
}

.back-button {
  padding: 10px 15px;
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
  font-weight: bold;
  font-size: 1rem;
  position: absolute;
  top: 10px;
  left: 10px;
}

.back-button:hover {
  background-color: #e0e0e0;
}

.subsections-grid {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: center;
}

.subsection-card {
  width: 300px;
  display: flex;
  padding: var(--dl-space-space-unit);
  margin: var(--dl-space-space-unit);
  align-items: flex-start;
  border-radius: 8px;
  flex-direction: column;
  background-color: var(--dl-color-gray-white);
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
}

.subsection-title {
  font-size: 1.2rem;
  margin-bottom: var(--dl-space-space-halfunit);
}

.subsection-description {
  color: var(--dl-color-gray-500);
  margin-bottom: var(--dl-space-space-unit);
}

.subsection-button {
  color: white;
  cursor: pointer;
  padding: 10px 15px;
  align-self: flex-start;
  border-radius: 4px;
  background-color: #4CAF50; /* Changed to green for better visibility */
  border: none;
  font-weight: bold;
  font-size: 0.9rem;
}

.subsection-button:hover {
  background-color: #388E3C; /* Darker green on hover */
}

.subsection-documents {
  width: 100%;
  margin-top: var(--dl-space-space-unit);
  margin-bottom: var(--dl-space-space-unit);
}

.subsection-documents-title {
  font-size: 1rem;
  margin-bottom: var(--dl-space-space-halfunit);
  color: #333;
}

.document-icons {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: var(--dl-space-space-unit);
}

.document-icon-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 15px;
  border-radius: 12px;
  transition: all 0.3s ease;
  max-width: 140px;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.document-icon-wrapper:hover {
  background-color: #fff5f5;
  transform: translateY(-5px);
  box-shadow: 0 4px 16px rgba(220, 20, 60, 0.2);
}

.document-icon-large {
  font-size: 64px;
  color: #DC143C;
  margin-bottom: 12px;
  position: relative;
  filter: drop-shadow(0 2px 4px rgba(220, 20, 60, 0.3));
}

.document-icon-large i {
  display: block;
}

.document-icon-name {
  font-size: 0.85rem;
  text-align: center;
  color: #333;
  font-weight: 500;
  word-wrap: break-word;
  line-height: 1.3;
}

.section-documents {
  width: 100%;
  margin-top: var(--dl-space-space-twounits);
}

.documents-title {
  font-size: 1.5rem;
  margin-bottom: var(--dl-space-space-unit);
  text-align: center;
}

.documents-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.document-card {
  width: 300px;
  display: flex;
  padding: var(--dl-space-space-unit);
  margin: var(--dl-space-space-halfunit);
  align-items: center;
  border-radius: 8px;
  background-color: var(--dl-color-gray-white);
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
}

.document-icon {
  font-size: 24px;
  margin-right: var(--dl-space-space-unit);
  color: #2196F3; /* Blue for document icons */
}

.document-info {
  flex: 1;
}

.document-name {
  font-size: 1rem;
  margin-bottom: 4px;
}

.document-meta {
  font-size: 0.8rem;
  color: var(--dl-color-gray-500);
}

.document-actions {
  display: flex;
}

.document-action-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  color: #2196F3; /* Blue for action buttons */
}

.document-action-button:hover {
  color: #1976D2; /* Darker blue on hover */
}

/* PDF Viewer Dialog Styles */
.pdf-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.pdf-viewer-dialog {
  width: 90%;
  height: 90%;
  max-width: 1200px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.pdf-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background-color: #f8f9fa;
}

.pdf-viewer-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.pdf-viewer-content {
  flex: 1;
  overflow: hidden;
  background-color: #525659;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.pdf-viewer-footer {
  display: flex;
  justify-content: center;
  padding: 15px 20px;
  border-top: 1px solid #eee;
  background-color: #f8f9fa;
}

.download-pdf-button {
  padding: 10px 20px;
  background-color: #DC143C;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.download-pdf-button:hover {
  background-color: #B01030;
}

/* Custom Upload Dialog Styles */
.upload-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.upload-dialog {
  width: 500px;
  max-width: 90%;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.upload-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.upload-dialog-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
}

.upload-dialog-content {
  padding: 20px;
}

.upload-section-name {
  margin-bottom: 15px;
  font-size: 1rem;
}

.upload-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.file-upload-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.file-input {
  display: none;
}

.file-select-button {
  padding: 10px 15px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-weight: bold;
}

.file-select-button:hover {
  background-color: #1976D2;
}

.selected-filename {
  font-size: 0.9rem;
  color: #666;
  margin-top: 5px;
}

.upload-dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding: 15px 20px;
  border-top: 1px solid #eee;
  gap: 10px;
}

.cancel-button {
  padding: 10px 15px;
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.cancel-button:hover {
  background-color: #e0e0e0;
}

.upload-submit-button {
  padding: 10px 15px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.upload-submit-button:hover:not(:disabled) {
  background-color: #1976D2;
}

.upload-submit-button:disabled {
  background-color: #bbdefb;
  cursor: not-allowed;
}

.normativa-quote {
  font-size: 2rem;
  text-align: center;
  margin-top: var(--dl-space-space-fourunits);
  margin-bottom: var(--dl-space-space-fourunits);
}

@media(max-width: 767px) {
  .portal-sections {
    padding-left: var(--dl-space-space-twounits);
    padding-right: var(--dl-space-space-twounits);
  }

  .section-card {
    width: 100%;
  }

  .section-detail {
    padding-left: var(--dl-space-space-twounits);
    padding-right: var(--dl-space-space-twounits);
  }

  .subsection-card {
    width: 100%;
  }

  .document-card {
    width: 100%;
  }
}

@media(max-width: 479px) {
  .portal-sections {
    padding-left: var(--dl-space-space-unit);
    padding-right: var(--dl-space-space-unit);
  }

  .section-detail {
    padding-left: var(--dl-space-space-unit);
    padding-right: var(--dl-space-space-unit);
  }

  .upload-dialog {
    width: 95%;
  }
}
</style>
